import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Select,
  Input,
  Textarea,
  useToast,
  Alert,
  AlertIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  FormErrorMessage,
  useColorModeValue,
  Spinner,
  Center,
  Divider,
  Badge,
  Box
} from '@chakra-ui/react';
import { ChevronRightIcon, CalendarIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const CreateSchedule = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    entity_type: searchParams.get('entity_type') || '',
    entity_id: searchParams.get('entity_id') || '',
    version_id: searchParams.get('version_id') || '',
    action: 'publish',
    scheduled_at: '',
    notes: ''
  });
  const [entities, setEntities] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedEntity, setSelectedEntity] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId && formData.entity_type) {
      fetchEntities();
    }
  }, [tenantId, formData.entity_type]);

  // Add this after the existing useEffect hooks to debug the data flow:
useEffect(() => {
  console.log('Current form data:', formData);
  console.log('Available entities:', entities);
  console.log('Available versions:', versions);
  console.log('Selected entity:', selectedEntity);
  console.log('Tenant ID:', tenantId);
}, [formData, entities, versions, selectedEntity, tenantId]);

  useEffect(() => {
    if (formData.entity_id && formData.entity_type === 'page') {
      fetchVersions();
    }
  }, [formData.entity_id, formData.entity_type]);

  useEffect(() => {
    if (formData.entity_id) {
      fetchEntityDetails();
    }
  }, [formData.entity_id, formData.entity_type]);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      let endpoint = '';
      
      switch (formData.entity_type) {
        case 'page':
          endpoint = `/pages/${tenantId}`;
          break;
        case 'content_entry':
          endpoint = `/content-entries/${tenantId}`;
          break;
        case 'navigation':
          endpoint = `/navigations/${tenantId}`;
          break;
        default:
          return;
      }

      console.log('Fetching entities from:', endpoint);
      const response = await apiClient(endpoint);
      console.log('Raw API response:', response);
      
      let entitiesData = [];
      
      // Handle different response structures
      if (response && response.success && Array.isArray(response.data)) {
        // Format: { success: true, data: [...] }
        entitiesData = response.data;
      } else if (response && response.status === 'success' && Array.isArray(response.data)) {
        // Format: { status: "success", data: [...] } - for content entries
        entitiesData = response.data;
      } else if (Array.isArray(response)) {
        // Direct array response
        entitiesData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: if there's a data property
        entitiesData = response.data;
      } else {
        console.warn('Unexpected response structure:', response);
        entitiesData = [];
      }
      
      // Filter out any invalid entries and ensure we have required properties
      entitiesData = entitiesData.filter(entity => 
        entity && 
        typeof entity === 'object' && 
        entity.id && 
        (entity.name || entity.title || entity.default_title)
      );
      
      console.log('Processed entities:', entitiesData);
      setEntities(entitiesData);
      
      if (entitiesData.length === 0) {
        toast({
          title: 'No entities found',
          description: `No ${formData.entity_type} entities found for this tenant`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
      
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast({
        title: 'Error loading entities',
        description: error.message || 'Failed to load entities',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setEntities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      console.log('Fetching versions for page:', formData.entity_id);
      const response = await apiClient(`/pages/${tenantId}/${formData.entity_id}/versions`);
      console.log('Versions response:', response);
      
      let versionsData = [];
      
      // Handle the response structure
      if (response && response.success && Array.isArray(response.data)) {
        versionsData = response.data;
      } else if (Array.isArray(response)) {
        versionsData = response;
      } else if (response && response.versions && Array.isArray(response.versions)) {
        versionsData = response.versions;
      }
      
      console.log('Processed versions:', versionsData);
      setVersions(versionsData);
      
    } catch (error) {
      console.error('Error fetching versions:', error);
      setVersions([]);
    }
  };

  const fetchEntityDetails = async () => {
    try {
      let endpoint = '';
      
      switch (formData.entity_type) {
        case 'page':
          endpoint = `/pages/${tenantId}/${formData.entity_id}`;
          break;
        case 'content_entry':
          endpoint = `/content-entries/${tenantId}/${formData.entity_id}`;
          break;
        case 'navigation':
          endpoint = `/navigations/${tenantId}/${formData.entity_id}`;
          break;
        default:
          return;
      }

      console.log('Fetching entity details from:', endpoint);
      const response = await apiClient(endpoint);
      console.log('Entity details response:', response);
      
      let entityData = null;
      
      // Handle the response structure
      if (response && response.success && response.data) {
        entityData = response.data;
      } else if (response && response.status === 'success' && response.data) {
        // Handle content entries response format
        entityData = response.data;
      } else if (response && response.id) {
        // Direct entity response
        entityData = response;
      } else if (response && typeof response === 'object' && !Array.isArray(response)) {
        // Fallback: assume response is the entity itself
        entityData = response;
      }
      
      console.log('Processed entity details:', entityData);
      setSelectedEntity(entityData);
      
    } catch (error) {
      console.error('Error fetching entity details:', error);
      setSelectedEntity(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.entity_type) {
      newErrors.entity_type = 'Entity type is required';
    }

    if (!formData.entity_id) {
      newErrors.entity_id = 'Please select an entity';
    }

    if (!formData.scheduled_at) {
      newErrors.scheduled_at = 'Scheduled date/time is required';
    } else {
      const scheduledDate = new Date(formData.scheduled_at);
      const now = new Date();
      if (scheduledDate <= now) {
        newErrors.scheduled_at = 'Scheduled time must be in the future';
      }
    }

    if (!formData.action) {
      newErrors.action = 'Action is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      console.log('Submitting schedule data:', formData);
      
      const response = await apiClient(`/schedule/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('Schedule response:', response);

      // Handle different success response formats
      if (response && (response.success || response.status === 'success' || response.id)) {
        toast({
          title: 'Publication scheduled',
          description: 'Your publication has been scheduled successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate('/scheduling');
      } else {
        throw new Error(response?.message || response?.error || 'Failed to schedule publication');
      }
    } catch (error) {
      console.error('Error scheduling publication:', error);
      toast({
        title: 'Error scheduling publication',
        description: error.message || 'Failed to schedule publication',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Minimum 5 minutes from now
    return now.toISOString().slice(0, 16);
  };

  const getEntityDisplayName = (entity) => {
    switch (formData.entity_type) {
      case 'page':
        return entity.name || `Page #${entity.id}`;
      case 'content_entry':
        // For content entries, use default_title or title from first translation
        return entity.default_title || 
               entity.title || 
               (entity.translations && entity.translations[0] && entity.translations[0].title) ||
               `Content Entry #${entity.id}`;
      case 'navigation':
        return entity.name || `Navigation #${entity.id}`;
      default:
        return `${formData.entity_type} #${entity.id}`;
    }
  };

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/scheduling">Scheduling</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Schedule Publication</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <HStack spacing={3} mb={2}>
                <CalendarIcon color="blue.500" />
                <Heading size="lg">Schedule Publication</Heading>
              </HStack>
              <Text color="gray.500">
                Schedule content to be published automatically at a specific time
              </Text>
            </Box>
            <HStack>
              <Button 
                variant="outline" 
                onClick={() => navigate('/scheduling')}
                isDisabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={saving}
                loadingText="Scheduling..."
              >
                Schedule Publication
              </Button>
            </HStack>
          </HStack>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Publication Details</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.entity_type}>
                  <FormLabel>Content Type</FormLabel>
                  <Select
                    value={formData.entity_type}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        entity_type: e.target.value,
                        entity_id: '',
                        version_id: ''
                      });
                      setEntities([]);
                      setVersions([]);
                      setSelectedEntity(null);
                      if (errors.entity_type) {
                        setErrors({ ...errors, entity_type: null });
                      }
                    }}
                    placeholder="Select content type"
                    isDisabled={saving}
                  >
                    <option value="page">Page</option>
                    <option value="content_entry">Content Entry</option>
                    <option value="navigation">Navigation</option>
                  </Select>
                  <FormErrorMessage>{errors.entity_type}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.entity_id}>
                  <FormLabel>Select Content</FormLabel>
                  {loading ? (
                    <Center py={4}>
                      <Spinner size="sm" />
                    </Center>
                  ) : (
                    <Select
                      value={formData.entity_id}
                      onChange={(e) => {
                        setFormData({ 
                          ...formData, 
                          entity_id: e.target.value,
                          version_id: ''
                        });
                        if (errors.entity_id) {
                          setErrors({ ...errors, entity_id: null });
                        }
                      }}
                      placeholder="Select content to schedule"
                      isDisabled={!formData.entity_type || saving}
                    >
                      {console.log('Selected Available entities:', entities)} {/* Debug log */}
                      {entities.map((entity) => (
                        <option key={entity.id} value={entity.id}>
                          {getEntityDisplayName(entity)}
                        </option>
                      ))}
                    </Select>
                  )}
                  <FormErrorMessage>{errors.entity_id}</FormErrorMessage>
                </FormControl>

                {formData.entity_type === 'page' && versions.length > 0 && (
                  <FormControl>
                    <FormLabel>Version (Optional)</FormLabel>
                    <Select
                      value={formData.version_id}
                      onChange={(e) => setFormData({ ...formData, version_id: e.target.value })}
                      placeholder="Select specific version (latest if not selected)"
                      isDisabled={saving}
                    >
                      {versions.map((version) => (
                        <option key={version.id} value={version.id}>
                          Version {version.version_number} - {version.version_type} 
                          ({new Date(version.created_at).toLocaleDateString()})
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedEntity && (
                  <Box p={4} bg="gray.50" borderRadius="md" width="100%">
                    <Text fontWeight="medium" mb={2}>Selected Content:</Text>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm">
                        <strong>Name:</strong> {getEntityDisplayName(selectedEntity)}
                      </Text>
                      {selectedEntity.status && (
                        <HStack>
                          <Text fontSize="sm"><strong>Status:</strong></Text>
                          <Badge colorScheme={selectedEntity.status === 'published' ? 'green' : 'yellow'}>
                            {selectedEntity.status}
                          </Badge>
                        </HStack>
                      )}
                      {selectedEntity.path && (
                        <Text fontSize="sm">
                          <strong>Path:</strong> {selectedEntity.path}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Schedule Settings</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.action}>
                  <FormLabel>Action</FormLabel>
                  <Select
                    value={formData.action}
                    onChange={(e) => {
                      setFormData({ ...formData, action: e.target.value });
                      if (errors.action) {
                        setErrors({ ...errors, action: null });
                      }
                    }}
                    isDisabled={saving}
                  >
                    <option value="publish">Publish</option>
                    <option value="unpublish">Unpublish</option>
                    <option value="archive">Archive</option>
                  </Select>
                  <FormErrorMessage>{errors.action}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.scheduled_at}>
                  <FormLabel>Scheduled Date & Time</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => {
                      setFormData({ ...formData, scheduled_at: e.target.value });
                      if (errors.scheduled_at) {
                        setErrors({ ...errors, scheduled_at: null });
                      }
                    }}
                    min={getMinDateTime()}
                    isDisabled={saving}
                  />
                  <FormErrorMessage>{errors.scheduled_at}</FormErrorMessage>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Publication will be processed within a few minutes of the scheduled time
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add any notes about this scheduled publication..."
                    rows={3}
                    isDisabled={saving}
                  />
                </FormControl>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  );
};

export default CreateSchedule;